import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Search, Film, Image as ImageIcon, Check, ChevronDown, Download, Link2, ShieldCheck, Trash2, Globe, EyeOff, Eye, HardDrive, LogIn, CheckCircle2, XCircle, Clock, Tag, StopCircle, FileCode2, UploadCloud } from "lucide-react";
import { toast } from "sonner";

function fmtDur(s) {
  if (!s) return null;
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}
function fmtSize(b) {
  if (!b) return null;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const POPULAR = ["xhamster", "pornhub", "xvideos", "redtube", "spankbang", "youporn", "reddit", "youtube", "vimeo", "+1800 more"];

export default function ImportUrl() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "drive" ? "drive" : "url"); // "url" | "drive" | "page"
  const [url, setUrl] = useState("");
  const [driveUrl, setDriveUrl] = useState("");
  const [showConnect, setShowConnect] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [siteUser, setSiteUser] = useState("");
  const [sitePass, setSitePass] = useState("");
  const [cookies, setCookies] = useState("");
  const [tags, setTags] = useState("");
  const [privateImport, setPrivateImport] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);      // normalized render items {key,title,thumb,type,dur,sub,raw}
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [sourceTitle, setSourceTitle] = useState("");
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [deepCrawl, setDeepCrawl] = useState(false);
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [crawlMaxPages, setCrawlMaxPages] = useState(300);
  const [crawlMaxMedia, setCrawlMaxMedia] = useState(10000);
  const [crawl, setCrawl] = useState(null); // {id, status, pages_visited, found, count}

  // resume an in-progress import on mount
  useEffect(() => {
    api.get("/import-jobs", { params: { limit: 5 } })
      .then(({ data }) => {
        const running = (data || []).find((j) => ["running", "cancelling"].includes(j.status));
        if (running) { setJob(running); setJobId(running.id); }
      })
      .catch(() => {});
  }, []);

  // poll the active import job
  useEffect(() => {
    if (!jobId) return;
    let stop = false;
    const done = (s) => ["completed", "cancelled", "failed"].includes(s);
    const tick = async () => {
      try {
        const { data } = await api.get(`/import-jobs/${jobId}`);
        if (stop) return;
        setJob(data);
        if (done(data.status)) return;
      } catch { /* ignore transient */ }
      if (!stop) setTimeout(tick, 1500);
    };
    tick();
    return () => { stop = true; };
  }, [jobId]);

  const [cancelling, setCancelling] = useState(false);
  const forceStop = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await api.post(`/import-jobs/${jobId}/cancel`);
      toast.success("Stopping import… it'll halt after the current item");
      setJob((j) => (j ? { ...j, status: "cancelling" } : j));
    } catch {
      toast.error("Couldn't stop the import");
    } finally {
      setCancelling(false);
    }
  };

  const reset = () => { setItems([]); setSelected(new Set()); };
  const switchMode = (m) => { setMode(m); reset(); setHtmlFiles([]); setCrawl(null); };

  // poll the active deep-crawl, streaming discovered media into the grid
  useEffect(() => {
    if (!crawl?.id || ["completed", "cancelled", "failed"].includes(crawl.status)) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/scrape/crawl/${crawl.id}`);
        if (stop) return;
        setCrawl(data);
        if (data.items?.length) applyCrawlItems(data);
        if (["completed", "cancelled", "failed"].includes(data.status)) {
          if (data.status === "failed") toast.error(data.reason || "Crawl failed");
          else toast.success(`Crawl ${data.status} — found ${data.count || (data.items || []).length} media across ${data.pages_visited} page(s)`);
          return;
        }
      } catch { /* transient */ }
      if (!stop) setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
  }, [crawl?.id, crawl?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseHtml = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.html?$/i.test(f.name));
    if (!files.length) { toast.error("Drop .html file(s) saved from a webpage"); return; }
    setScanning(true); reset(); setHtmlFiles(files);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/scrape/parse-html", fd);
      if (data.status !== "ok") { toast.error(data.reason || "No video links found in this HTML"); return; }
      const norm = data.items.map((it) => ({ key: `h${it.index}`, title: it.title || "video", thumb: null, type: "video", dur: null, sub: null, raw: it }));
      setItems(norm);
      setSelected(norm.length <= 20 ? new Set(norm.map((i) => i.key)) : new Set());
      toast.success(`Found ${norm.length} video link${norm.length === 1 ? "" : "s"} — select what to import`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't parse the HTML file");
    } finally {
      setScanning(false);
    }
  };

  const scan = async (e) => {
    e?.preventDefault();
    setScanning(true);
    reset();
    try {
      let norm = [];
      let srcTitle = "", isPlaylist = false;
      if (mode === "url") {
        if (!url.trim()) { toast.error("Paste a URL to scan"); return; }
        const { data } = await api.post("/scrape/preview", { url, limit: 200, cookies: cookies || null, username: siteUser || null, password: sitePass || null });
        if (data.status !== "ok") { toast.error(data.reason || "Nothing found"); return; }
        norm = data.items.map((it) => ({ key: `u${it.index}`, title: it.title || "Untitled", thumb: it.thumbnail, type: it.media_type, dur: it.duration, sub: it.uploader, raw: it }));
        srcTitle = (data.source_title || "").replace(/,/g, " ").trim();
        isPlaylist = !!data.is_playlist;
      } else if (mode === "page") {
        if (!url.trim()) { toast.error("Paste a page URL to scan"); return; }
        if (deepCrawl) { setScanning(false); return startCrawl(); }
        const { data } = await api.post("/scrape/scan", { url, cookies: cookies || null });
        if (data.status !== "ok") { toast.error(data.reason || "No media found on this page"); return; }
        norm = data.items.map((it, i) => ({ key: `p${i}`, title: it.name || "media", thumb: it.thumb, type: it.type, dur: null, sub: null, raw: it }));
      } else {
        if (!driveUrl.trim()) { toast.error("Paste a Google Drive folder link"); return; }
        const { data } = await api.post("/drive/preview", { url: driveUrl });
        if (data.status !== "ok") { toast.error(data.reason || "Nothing found"); return; }
        norm = data.items.map((it) => ({ key: it.file_id, title: it.name || "Untitled", thumb: it.thumbnail, type: it.media_type, dur: null, sub: fmtSize(it.size), raw: it }));
      }
      setItems(norm);
      if (mode === "url") {
        // channel / playlist / model page → auto-select every video for one-tap mass import
        setSelected(new Set(norm.map((i) => i.key)));
        // auto-tag every video in the batch with the playlist / channel name
        if (isPlaylist && srcTitle) {
          setSourceTitle(srcTitle);
          if (!tags.trim()) setTags(srcTitle);
        } else {
          setSourceTitle("");
        }
        if (norm.length > 1) toast.success(`Playlist detected — all ${norm.length} videos selected${srcTitle ? `, auto-tagged "${srcTitle}"` : ""}.`);
        else toast.success(`Found ${norm.length} item`);
      } else if (norm.length === 1) {
        setSelected(new Set([norm[0].key]));
        toast.success("Found 1 item");
      } else if (norm.length <= 20) {
        setSelected(new Set(norm.map((i) => i.key))); // small batch: preselect all
        toast.success(`Found ${norm.length} items`);
      } else {
        setSelected(new Set()); // large folder: start empty to avoid accidental huge imports
        toast.success(`Found ${norm.length} items — select the ones you want`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const toggle = (key) => setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const applyCrawlItems = (data) => {
    const norm = (data.items || []).map((it, i) => ({ key: `c${i}`, title: it.name || "media", thumb: it.thumb, type: it.type, dur: null, sub: null, raw: it }));
    setItems(norm);
    setSelected(new Set());
    if (data.title && !sourceTitle) setSourceTitle(data.title);
  };

  const startCrawl = async () => {
    reset();
    try {
      const { data } = await api.post("/scrape/crawl", {
        url, cookies: cookies || null, depth: Number(crawlDepth) || 2,
        max_pages: Number(crawlMaxPages) || 300, max_media: Number(crawlMaxMedia) || 10000,
      });
      setCrawl({ id: data.job_id, status: "running", pages_visited: 0, found: 0, count: 0 });
      toast.success("Deep crawl started — media will stream in as pages are scanned.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start crawl");
    }
  };

  const stopCrawl = async () => {
    if (!crawl?.id) return;
    try { await api.post(`/scrape/crawl/${crawl.id}/cancel`); toast("Stopping crawl…"); } catch {}
  };

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.key)));

  const doImport = async () => {
    const chosen = items.filter((i) => selected.has(i.key)).map((i) => i.raw);
    if (chosen.length === 0) return toast.error("Select at least one item");
    if (chosen.length > 50 && !window.confirm(`Import all ${chosen.length} items? Large batches download in the background and can take a while.`)) return;
    setImporting(true);
    try {
      let data;
      if (mode === "url" || mode === "html") {
        ({ data } = await api.post("/scrape/import", { items: chosen, tags, cookies: cookies || null, private: privateImport, username: siteUser || null, password: sitePass || null }));
      } else if (mode === "page") {
        ({ data } = await api.post("/scrape/import-urls", { urls: chosen.map((c) => c.url), tags, cookies: cookies || null, private: privateImport, source_url: url }));
      } else {
        ({ data } = await api.post("/drive/import", { items: chosen, tags, private: privateImport }));
      }
      toast.success(`Importing ${data.queued} item${data.queued === 1 ? "" : "s"} — ${privateImport ? "saved as private drafts for review." : "they'll appear shortly with auto-tags."}`);
      if ((mode === "url" || mode === "html") && data.job_id) {
        // keep the user here to watch live progress
        setJob(null);
        setJobId(data.job_id);
        setItems([]); setSelected(new Set());
      } else {
        setTimeout(() => nav(privateImport && user ? `/u/${user.username}` : "/explore"), 1200);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-20 pb-16" data-testid="import-url-page">
      <div className="flex items-center gap-3 mb-2">
        <Download className="text-[#0066FF]" size={30} />
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white">Import <span className="text-[#0066FF]">media</span></h1>
      </div>

      <div className="flex gap-2 mb-6">
        <button data-testid="import-mode-url" onClick={() => switchMode("url")} className={`flex items-center gap-2 px-4 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "url" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>
          <Link2 size={14} /> From URL
        </button>
        <button data-testid="import-mode-drive" onClick={() => switchMode("drive")} className={`flex items-center gap-2 px-4 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "drive" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>
          <HardDrive size={14} /> Google Drive folder
        </button>
        <button data-testid="import-mode-page" onClick={() => switchMode("page")} className={`flex items-center gap-2 px-4 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "page" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>
          <Globe size={14} /> Scan page
        </button>
        <button data-testid="import-mode-html" onClick={() => switchMode("html")} className={`flex items-center gap-2 px-4 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "html" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>
          <FileCode2 size={14} /> HTML file
        </button>
      </div>

      {mode === "url" ? (
        <>
          <p className="text-[#A1A1AA] text-sm mb-3 max-w-2xl">Paste a single video, or a whole <b className="text-[#FFFFFF]">channel / model / playlist / tag page</b> — we'll list every video and auto-select them all so you can mass-import in one tap.</p>
          <div className="flex flex-wrap items-center gap-1.5 mb-6" data-testid="import-url-supported">
            <Globe size={12} className="text-[#52525B]" />
            {POPULAR.map((s) => (<span key={s} className="font-mono text-[9px] uppercase tracking-wider text-[#52525B] border border-[#1C1C1F] bg-[#0A0A0A] px-2 py-0.5">{s}</span>))}
          </div>
        </>
      ) : mode === "page" ? (
        <p className="text-[#A1A1AA] text-sm mb-6 max-w-2xl">Paste any web page URL. We'll scan its HTML and list <b>every image & video</b> we find on it — pick what you want and it's auto-tagged on import. For login-only pages, add your cookies below.</p>
      ) : mode === "html" ? (
        <p className="text-[#A1A1AA] text-sm mb-6 max-w-2xl">Upload a <b className="text-white">saved webpage</b> (a <span className="font-mono text-white">.html</span> file you saved from your browser). We'll read it, pull out every <b>video link</b> inside, and let you pick which ones to download through the importer. Drop several files at once to combine them.</p>
      ) : (
        <p className="text-[#A1A1AA] text-sm mb-6 max-w-2xl">Paste a Google Drive <b>folder</b> link. The folder must be shared as <span className="font-mono text-[#FFFFFF]">"Anyone with the link"</span>. We'll pull every photo & video (including subfolders) — pick what you want and it's auto-tagged on import.</p>
      )}

      {mode === "html" ? (
        <div className="mb-6">
          <label
            data-testid="import-html-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); parseHtml(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-16 px-6 cursor-pointer transition-colors ${dragOver ? "border-[#0066FF] bg-[#0A2540]" : "border-[#1C1C1F] bg-[#0A0A0A] hover:border-[#52525B]"}`}
          >
            <input data-testid="import-html-input" type="file" accept=".html,.htm,text/html" multiple className="hidden" onChange={(e) => parseHtml(e.target.files)} />
            {scanning ? <Loader2 className="animate-spin text-[#0066FF]" size={34} /> : <UploadCloud className="text-[#0066FF]" size={34} />}
            <p className="font-heading uppercase tracking-widest text-xs text-white">Drop .html file(s) here, or click to browse</p>

            {htmlFiles.length > 0 && (
              <p data-testid="import-html-filenames" className="text-[#A1A1AA] font-mono text-[11px] mt-1 flex items-center gap-1.5"><FileCode2 size={12} /> {htmlFiles.map((f) => f.name).join(", ")}</p>
            )}
          </label>
        </div>
      ) : (
      <form onSubmit={scan} className="space-y-3 mb-6">
        <div className="flex gap-2">
          {mode === "drive" ? (
            <input data-testid="import-drive-input" value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} placeholder="https://drive.google.com/drive/folders/…" className="flex-1 bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-sm" />
          ) : (
            <input data-testid="import-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={mode === "page" ? "https://…  (any web page to scan)" : "https://…  (a video, channel, playlist)"} className="flex-1 bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-sm" />
          )}
          <button data-testid="import-url-scan-btn" disabled={scanning} className="bg-[#0066FF] text-white font-heading uppercase tracking-widest text-xs px-6 hover:bg-[#0055CC] transition-colors disabled:opacity-50 flex items-center gap-2">
            {scanning ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} Scan
          </button>
        </div>

        {mode === "page" && (
          <div className="border border-[#1C1C1F] bg-[#0A0A0A] rounded-xl p-4" data-testid="import-crawl-panel">
            <button type="button" onClick={() => setDeepCrawl((v) => !v)} data-testid="import-crawl-toggle" className="flex items-center gap-3 w-full text-left">
              <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${deepCrawl ? "bg-[#0066FF]" : "bg-[#1C1C1F]"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${deepCrawl ? "translate-x-5" : ""}`} />
              </span>
              <span>
                <span className="flex items-center gap-2 text-white font-heading uppercase tracking-widest text-xs"><Globe size={14} className="text-[#0066FF]" /> Deep crawl (render + scroll + follow links)</span>
                <span className="block text-[#52525B] font-mono text-[10px] mt-1">Loads the page in a real browser, scrolls to force lazy images, then follows same-site links to grab everything.</span>
              </span>
            </button>
            {deepCrawl && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                <label className="flex flex-col gap-1"><span className="text-[#52525B] text-[10px] font-mono uppercase">Link depth</span>
                  <input data-testid="import-crawl-depth" type="number" min={0} max={4} value={crawlDepth} onChange={(e) => setCrawlDepth(e.target.value)} className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#0066FF]" /></label>
                <label className="flex flex-col gap-1"><span className="text-[#52525B] text-[10px] font-mono uppercase">Max pages</span>
                  <input data-testid="import-crawl-pages" type="number" min={1} max={2000} value={crawlMaxPages} onChange={(e) => setCrawlMaxPages(e.target.value)} className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#0066FF]" /></label>
                <label className="flex flex-col gap-1"><span className="text-[#52525B] text-[10px] font-mono uppercase">Max media</span>
                  <input data-testid="import-crawl-media" type="number" min={1} max={20000} value={crawlMaxMedia} onChange={(e) => setCrawlMaxMedia(e.target.value)} className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#0066FF]" /></label>
              </div>
            )}
          </div>
        )}

        {(mode === "url" || mode === "page") && (
          <>
            {mode === "url" && (
              <>
                <button type="button" onClick={() => setShowLogin((s) => !s)} data-testid="import-url-login-toggle" className="flex items-center gap-1.5 text-[#A1A1AA] hover:text-white font-mono text-[11px] uppercase tracking-wider transition-colors">
                  <ChevronDown size={13} className={`transition-transform ${showLogin ? "rotate-180" : ""}`} />
                  {(siteUser.trim() || sitePass.trim()) ? <span className="flex items-center gap-1.5 text-[#3fb950]"><LogIn size={13} /> Site login attached</span> : "Members-only video? Sign in with the site's username & password (no cookies)"}
                </button>
                {showLogin && (
                  <div className="border border-[#1C1C1F] bg-[#0A0A0A] p-4 space-y-3" data-testid="import-url-login-panel">
                    <div className="flex items-center gap-2 text-[#FFFFFF] text-sm font-heading uppercase tracking-widest"><LogIn size={15} className="text-[#0066FF]" /> Sign in to the site</div>
                    <p className="text-[#A1A1AA] text-xs">Enter your account on the source site — we log in with yt-dlp to pull members-only videos. Used only for this import, <b>never stored</b>. Note: yt-dlp can log into a subset of sites; if it fails, use cookies below.</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input data-testid="import-url-site-user" value={siteUser} onChange={(e) => setSiteUser(e.target.value)} autoComplete="off" placeholder="Site username / email" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-xs" />
                      <input data-testid="import-url-site-pass" value={sitePass} onChange={(e) => setSitePass(e.target.value)} type="password" autoComplete="new-password" placeholder="Site password" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-xs" />
                    </div>
                  </div>
                )}
              </>
            )}
            <button type="button" onClick={() => setShowConnect((s) => !s)} data-testid="import-url-connect-toggle" className="flex items-center gap-1.5 text-[#A1A1AA] hover:text-white font-mono text-[11px] uppercase tracking-wider transition-colors">
              <ChevronDown size={13} className={`transition-transform ${showConnect ? "rotate-180" : ""}`} />
              {cookies.trim() ? <span className="flex items-center gap-1.5 text-[#3fb950]"><ShieldCheck size={13} /> Cookies attached</span> : "Age-restricted or login-only? Add cookies (optional)"}
            </button>
            {showConnect && (
              <div className="border border-[#1C1C1F] bg-[#0A0A0A] p-4 space-y-3" data-testid="import-url-connect-panel">
                <div className="flex items-center gap-2 text-[#FFFFFF] text-sm font-heading uppercase tracking-widest"><ShieldCheck size={15} className="text-[#0066FF]" /> Cookies (cookies.txt)</div>
                <p className="text-[#A1A1AA] text-xs">Export your browser cookies for the site in Netscape <span className="font-mono text-[#FFFFFF]">cookies.txt</span> format and paste them here. Used only for this import, never stored.</p>
                <textarea data-testid="import-url-cookies" value={cookies} onChange={(e) => setCookies(e.target.value)} placeholder="# Netscape HTTP Cookie File" rows={4} className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-[11px] resize-none" />
                <button type="button" onClick={() => setShowHelp((h) => !h)} data-testid="import-url-help-toggle" className="flex items-center gap-1.5 text-[#52525B] hover:text-[#A1A1AA] font-mono text-[10px] uppercase tracking-wider transition-colors"><ChevronDown size={12} className={`transition-transform ${showHelp ? "rotate-180" : ""}`} /> How do I export cookies.txt?</button>
                {showHelp && (<ol className="text-[#A1A1AA] text-[11px] leading-relaxed list-decimal pl-4 space-y-1"><li>Install a browser extension like <span className="font-mono text-[#FFFFFF]">Get cookies.txt LOCALLY</span>.</li><li>Log in to the site and confirm you can view the content.</li><li>Open the extension on that tab and export.</li><li>Paste the whole text above.</li></ol>)}
              </div>
            )}
          </>
        )}
      </form>
      )}

      {job && (
        <div className="border border-[#1C1C1F] bg-[#0A0A0A] mb-6" data-testid="import-progress-panel">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#1C1C1F]">
            <div className="flex items-center gap-2 min-w-0">
              {job.status === "completed" ? <CheckCircle2 size={18} className="text-[#3fb950]" />
                : job.status === "cancelled" ? <XCircle size={18} className="text-[#e57373]" />
                : <Loader2 size={18} className="animate-spin text-[#0066FF]" />}
              <span className="font-heading uppercase tracking-widest text-xs text-white">
                {job.status === "completed" ? "Import finished"
                  : job.status === "cancelled" ? "Import stopped"
                  : job.status === "cancelling" ? "Stopping…"
                  : "Importing…"}
              </span>
              <span className="font-mono text-[11px] text-[#A1A1AA]" data-testid="import-progress-count">
                {job.done + job.failed} / {job.total} done{job.failed ? ` · ${job.failed} failed` : ""}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {["running", "cancelling"].includes(job.status) && (
                <button onClick={forceStop} disabled={cancelling || job.status === "cancelling"} data-testid="import-force-stop"
                  className="flex items-center gap-1.5 bg-[#e5484d] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-md hover:bg-[#d13438] transition-colors disabled:opacity-50">
                  <StopCircle size={13} /> {job.status === "cancelling" ? "Stopping…" : "Force stop"}
                </button>
              )}
              <button onClick={() => { setJob(null); setJobId(null); }} data-testid="import-progress-dismiss" className="text-[#52525B] hover:text-white transition-colors font-mono text-[10px] uppercase tracking-wider">Dismiss</button>
            </div>
          </div>
          <div className="px-4 pt-3">
            <div className="h-1.5 bg-[#1C1C1F] rounded-full overflow-hidden">
              <div className="h-full bg-[#0066FF] transition-all duration-500" style={{ width: `${job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-3 space-y-1" data-testid="import-progress-list">
            {job.items.map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`import-progress-item-${i}`}>
                {it.status === "done" ? <CheckCircle2 size={14} className="text-[#3fb950] shrink-0" />
                  : it.status === "failed" ? <XCircle size={14} className="text-[#0066FF] shrink-0" />
                  : it.status === "downloading" ? <Loader2 size={14} className="animate-spin text-[#f0a020] shrink-0" />
                  : <Clock size={14} className="text-[#52525B] shrink-0" />}
                <span className={`truncate ${it.status === "done" ? "text-[#FFFFFF]" : it.status === "failed" ? "text-[#e57373]" : it.status === "downloading" ? "text-white" : "text-[#52525B]"}`}>{it.title}</span>
                {it.status === "failed" && it.error && <span className="text-[#e57373]/60 font-mono text-[9px] truncate ml-auto shrink-0 max-w-[40%]" title={it.error}>{it.error}</span>}
              </div>
            ))}
          </div>
          {["completed", "cancelled"].includes(job.status) && user && (job.done > 0) && (
            <div className="px-4 py-3 border-t border-[#1C1C1F]">
              <button onClick={() => nav(job.private ? `/u/${user.username}` : "/explore")} data-testid="import-progress-view" className="bg-[#0066FF] text-white font-heading uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-[#0055CC] transition-colors flex items-center gap-2">
                <Check size={14} /> View imported {job.private ? "drafts" : "media"}
              </button>
            </div>
          )}
        </div>
      )}

      {crawl && (
        <div className="border border-[#1C1C1F] bg-[#0A0A0A] mb-6 rounded-xl" data-testid="import-crawl-progress">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              {crawl.status === "completed" ? <CheckCircle2 size={18} className="text-[#3fb950]" />
                : crawl.status === "cancelled" ? <XCircle size={18} className="text-[#e57373]" />
                : crawl.status === "failed" ? <XCircle size={18} className="text-[#e57373]" />
                : <Loader2 size={18} className="animate-spin text-[#0066FF]" />}
              <span className="font-heading uppercase tracking-widest text-xs text-white">
                {crawl.status === "running" ? "Crawling…" : `Crawl ${crawl.status}`}
              </span>
              <span className="font-mono text-[11px] text-[#A1A1AA]" data-testid="import-crawl-count">
                {crawl.pages_visited || 0} page(s) · {crawl.found ?? crawl.count ?? 0} media found
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {crawl.status === "running" && (
                <button onClick={stopCrawl} data-testid="import-crawl-stop" className="flex items-center gap-1.5 bg-[#e5484d] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-md hover:bg-[#d13438] transition-colors">
                  <StopCircle size={13} /> Stop crawl
                </button>
              )}
              <button onClick={() => setCrawl(null)} data-testid="import-crawl-dismiss" className="text-[#52525B] hover:text-white transition-colors font-mono text-[10px] uppercase tracking-wider">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {scanning && (
        <div className="flex flex-col items-center justify-center py-24 text-[#52525B]" data-testid="import-url-loading">
          <Loader2 className="animate-spin text-[#0066FF] mb-3" size={32} />
          <p className="font-mono text-xs uppercase tracking-widest">Scanning for media…</p>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4 border-y border-[#1C1C1F] py-3">
            <button data-testid="import-url-select-all" onClick={toggleAll} className="font-heading uppercase tracking-widest text-[11px] border border-[#1C1C1F] text-[#FFFFFF] hover:border-[#0066FF] hover:text-white transition-colors px-3 py-2">{allSelected ? "Deselect all" : "Select all"}</button>
            <span data-testid="import-url-selected-count" className="font-mono text-xs text-[#A1A1AA]">{selected.size} / {items.length} selected</span>
            <input data-testid="import-url-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags for all imports (comma separated)" className="flex-1 min-w-[200px] bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl font-mono text-xs" />
            <button type="button" data-testid="import-url-private-toggle" onClick={() => setPrivateImport((p) => !p)} aria-pressed={privateImport} title={privateImport ? "Imports are saved as private drafts you review before publishing" : "Imports go live publicly right away"} className={`flex items-center gap-1.5 font-heading uppercase tracking-widest text-[11px] border px-3 py-2 transition-colors ${privateImport ? "border-[#0066FF] text-[#0066FF] bg-[#0A2540]" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white hover:border-[#2A2A2E]"}`}>
              {privateImport ? <EyeOff size={13} /> : <Eye size={13} />} {privateImport ? "Private drafts" : "Public"}
            </button>
            <button data-testid="import-url-import-btn" onClick={doImport} disabled={importing || selected.size === 0} className="bg-[#0066FF] text-white font-heading uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-[#0055CC] transition-colors disabled:opacity-50 flex items-center gap-2">
              {importing ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Import {selected.size > 0 ? selected.size : ""}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="import-url-grid">
            {items.map((it) => {
              const sel = selected.has(it.key);
              return (
                <button key={it.key} onClick={() => toggle(it.key)} data-testid={`import-url-item-${it.key}`} className={`relative group text-left border bg-black aspect-[3/4] overflow-hidden transition-all ${sel ? "border-[#0066FF] ring-1 ring-[#0066FF]" : "border-[#1C1C1F] hover:border-[#52525B]"}`}>
                  {it.thumb ? (
                    <img src={it.thumb} alt={it.title} className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#2A2A2E]">{it.type === "image" ? <ImageIcon size={32} /> : <Film size={32} />}</div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 pt-6">
                    <p className="text-white text-[11px] font-semibold line-clamp-2 leading-tight">{it.title || "Untitled"}</p>
                    {it.sub && <p className="text-[#A1A1AA] font-mono text-[9px] truncate mt-0.5">{it.sub}</p>}
                  </div>
                  <div className="absolute top-2 left-2 bg-black/70 text-white px-1.5 py-0.5 font-mono text-[9px] flex items-center gap-1">{it.type === "image" ? <ImageIcon size={10} /> : <Film size={10} />} {it.type === "image" ? "IMG" : "VID"}</div>
                  {fmtDur(it.dur) && (<div className="absolute bottom-2 right-2 bg-black/70 text-white px-1.5 py-0.5 font-mono text-[9px]">{fmtDur(it.dur)}</div>)}
                  <div className={`absolute top-2 right-2 w-6 h-6 flex items-center justify-center border transition-all ${sel ? "bg-[#0066FF] border-[#0066FF]" : "bg-black/60 border-[#52525B] group-hover:border-white"}`}>{sel && <Check size={14} className="text-white" />}</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {!scanning && items.length === 0 && mode !== "html" && (
        <div className="border border-dashed border-[#1C1C1F] py-20 text-center" data-testid="import-url-empty">
          {mode === "url" ? <Link2 className="text-[#1C1C1F] mx-auto mb-3" size={40} /> : mode === "page" ? <Globe className="text-[#1C1C1F] mx-auto mb-3" size={40} /> : <HardDrive className="text-[#1C1C1F] mx-auto mb-3" size={40} />}
          <p className="text-[#52525B] font-mono text-xs uppercase tracking-widest">{mode === "url" ? "Paste a URL above to see its media here" : mode === "page" ? "Paste a web page URL above to scan it for media" : "Paste a public Drive folder link above"}</p>
        </div>
      )}
    </div>
  );
}
